/**
 * Same-origin, token-authed uploads into a claim's evidence-request session
 * (`ExternalUploadSession`). Both the standalone Upload Evidence page and the
 * consumer claim view post here (WPS-138 #6). The endpoints are `AllowAny` +
 * token-scoped, so any authenticated or anonymous surface holding the session
 * token can use them.
 */

import axios from 'axios'
import { buildApiUrl } from '@/shared/lib/api-origin'
import { unwrapApiEnvelope } from '@/shared/lib/api-envelope'
import type { UploadedFile } from '@/shared/components/evidence/types'

const sessionApi = axios.create({
  baseURL: buildApiUrl('/api/upload/sessions'),
  headers: { 'Content-Type': 'application/json' },
})

sessionApi.interceptors.response.use(unwrapApiEnvelope)

export interface SessionUploadProgress {
  loaded: number
  total: number
  percent: number
}

export interface SessionUploadParams {
  slotId: string
  file: File
  documentOptionKey?: string
  /** Stable across retries, so a retried upload never stores a second copy. */
  clientUploadId?: string
  onProgress?: (progress: SessionUploadProgress) => void
}

export async function uploadSessionFile(
  token: string,
  params: SessionUploadParams,
): Promise<UploadedFile> {
  const form = new FormData()
  form.append('slot_id', params.slotId)
  form.append('file', params.file)
  if (params.documentOptionKey) {
    form.append('document_option_key', params.documentOptionKey)
  }
  if (params.clientUploadId) form.append('client_upload_id', params.clientUploadId)
  const res = await sessionApi.post(`/${token}/files/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (e.total && params.onProgress) {
        params.onProgress({
          loaded: e.loaded,
          total: e.total,
          percent: Math.round((e.loaded / e.total) * 100),
        })
      }
    },
  })
  return res.data as UploadedFile
}

export async function deleteSessionFile(token: string, fileUuid: string): Promise<void> {
  await sessionApi.delete(`/${token}/files/${fileUuid}/`)
}

export interface SlotCopies {
  /** The hashes this session already holds in the slot. */
  held: Set<string>
  /** The most files the claim may hold, and how many more it can take (null: no limit). */
  limit: number | null
  room: number | null
}

/** Which of these file hashes this session already holds in the slot, and the claim's room. */
export async function checkSessionCopies(
  token: string,
  slotId: string,
  hashes: string[],
): Promise<SlotCopies> {
  if (hashes.length === 0) return { held: new Set(), limit: null, room: null }
  const res = await sessionApi.post(`/${token}/copies/`, {
    slot_id: slotId,
    content_sha256: hashes,
  })
  const data = res.data as {
    copies?: { content_sha256: string }[]
    limit?: number | null
    room?: number | null
  }
  return {
    held: new Set((data.copies ?? []).map((c) => c.content_sha256)),
    limit: data.limit ?? null,
    room: data.room ?? null,
  }
}

/** A requirement slot still short of its minimum when the session is submitted. */
export interface SessionShortfall {
  slot_uuid: string
  requirement_key: string
  label: string
  have: number
  need: number
}

export class SessionShortfallError extends Error {
  shortfalls: SessionShortfall[]

  constructor(shortfalls: SessionShortfall[]) {
    super('Not all required slots are filled')
    this.name = 'SessionShortfallError'
    this.shortfalls = shortfalls
  }
}

/**
 * Submit the session: the same finalise the Upload Evidence page runs. The
 * server refuses (422) while a required slot is short, listing the shortfalls.
 */
export async function finaliseSession(token: string): Promise<void> {
  try {
    await sessionApi.post(`/${token}/finalise/`, {})
  } catch (err: unknown) {
    const response = (
      err as {
        response?: {
          status?: number
          data?: { result?: { shortfalls?: SessionShortfall[] } }
        }
      }
    ).response
    if (response?.status === 422) {
      throw new SessionShortfallError(response.data?.result?.shortfalls ?? [])
    }
    throw err
  }
}

export default sessionApi
